/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  GetDetailRowDataParams,
  GridApi,
  GridReadyEvent,
  IRowNode,
  IServerSideGetRowsParams,
  NewValueParams,
  RefreshServerSideParams,
} from 'ag-grid-community';
import { computed, Directive, effect, Injector, OnInit, Type } from '@angular/core';
import { MaybeAsync } from '@angular/router';
import { StateSource } from '@ngrx/signals';
import { SignalStoreFeatureResult, StateSignals } from '@ngrx/signals/src/signal-store-models';
import { OmitPrivate, Prettify } from '@ngrx/signals/src/ts-helpers';
import { TypesaurusCoreDocDef } from '../../../repository/firebase/typesaurus.interface';
import { Observable } from 'rxjs';
import {
  AllColumnDefinition,
  ColumnDefinition,
  IDeptDefinition,
} from '../../../domains/table-grid/table-definition.gen.interface';
import {
  OnAgGridExpand,
  OnAgGridReady,
  OnAgGridRefreshCache,
  OnAgGridSelection,
  OnAgGridSetValue,
} from '../ag-grid.interface';

type SignalStoreMembers<FeatureResult extends SignalStoreFeatureResult> = Prettify<
  OmitPrivate<StateSignals<FeatureResult['state']> & FeatureResult['computed'] & FeatureResult['methods']>
>;

type StoreKeyValueMap<T extends { [K in keyof T]: new (...args: any[]) => any }> = {
  [K in keyof T]: {
    store: T[K];
    load?: (store: InstanceType<T[K]>) => Observable<void>;
  };
};

type StoreKeyValue<R extends SignalStoreFeatureResult, T extends new (...args: any[]) => R['state'] = any> = {
  store: T;
  get?: (store: InstanceType<T>) => Array<unknown>;
  update?: (store: InstanceType<T>, value: any, ref: any) => Observable<void>;
  load?: (store: InstanceType<T>) => Observable<void>;
  syncLoad?: (store: InstanceType<T>) => Observable<void>;
};

@Directive({ standalone: true })
export abstract class AbstractAgGridtableDirective<
  AllColumnsDef extends Pick<AllColumnDefinition, keyof AllColumnDefinition>,
  DeptDef extends IDeptDefinition,
  MainStoreInstance extends Type<SignalStoreMembers<R> & StateSource<Prettify<OmitPrivate<R['state']>>>> = any, //Type<SignalStoreProps<MergeFeatureResults<R>> & WritableStateSource<Prettify<MergeFeatureResults<R>['state']>>>
  OtherStoreInstances extends {
    [key: string]: Type<SignalStoreMembers<R> & StateSource<Prettify<OmitPrivate<R['state']>>>>; //Type<SignalStoreProps<MergeFeatureResults<R>> & WritableStateSource<Prettify<MergeFeatureResults<R>['state']>>>;
  } = any,
  R extends SignalStoreFeatureResult = any,
  MainStoreObj extends StoreKeyValue<R, MainStoreInstance> = StoreKeyValue<R, MainStoreInstance>,
  OtherStoreObj extends StoreKeyValueMap<OtherStoreInstances> = StoreKeyValueMap<OtherStoreInstances>,
>
  implements OnInit, OnAgGridReady, OnAgGridExpand, OnAgGridSetValue, OnAgGridSelection, OnAgGridRefreshCache
{
  private gridApi?: GridApi;
  public isColumnsReady = false;

  constructor(
    public injector: Injector,
    protected mainStore: MainStoreObj,
    private otherStore: OtherStoreObj,
    private deptDef: DeptDef,
    private allColumnsSchema: { defCallback: () => ColumnDefinition[]; name: keyof AllColumnsDef },
  ) {}

  public deptDefinition: Array<DeptDef> = [
    {
      ...this.deptDef,
      cellUpdate: (
        value: { [key: string]: TypesaurusCoreDocDef['Model'][keyof TypesaurusCoreDocDef['Model']] },
        ref,
      ) => {
        this.mainStore.update?.(this.mainStore.store as InstanceType<MainStoreInstance>, value, ref);
      },
    },
  ];
  public allColumns = computed<AllColumnsDef>(() => {
    const baseSchema = this.allColumnsSchema.defCallback();
    const ret = {
      [this.allColumnsSchema.name]: baseSchema.map((x) => ({
        ...x,
        colDef: {
          ...x.colDef,
          cellRenderer: x.colDef?.cellRenderer === 'agGroupCellRenderer' ? undefined : x.colDef?.cellRenderer,
        },
      })),
    } as AllColumnsDef;

    this.isColumnsReady = true;
    return ret;
  });

  onGridReady(event: GridReadyEvent<any, any>): MaybeAsync<void> {
    this.gridApi = event.api;
    this.gridApi.showLoadingOverlay();
    this.mainStore.load?.(this.mainStore.store as InstanceType<MainStoreInstance>);
    Object.values(this.otherStore).forEach((storeObj: StoreKeyValue<R>) =>
      storeObj.load?.(storeObj.store as InstanceType<OtherStoreInstances[keyof OtherStoreInstances]>),
    );

    if (Object.keys(this.otherStore).length > 0) {
      this.gridApi.setGridOption('context', { ...this.otherStore });
    }
    const datahandler = this.datasource();

    this.gridApi.setGridOption('serverSideDatasource', datahandler);
  }
  onGridExpand(
    _event: GetDetailRowDataParams<any, any> & { depth: number; depDef: IDeptDefinition },
  ): MaybeAsync<void> {
    throw new Error('Method not implemented.');
  }
  onGridSetValue(value: { value: NewValueParams<any, any>; depth: number; depDef: IDeptDefinition }): MaybeAsync<void> {
    // console.warn('onGridSetValue', value);
    const updateValues: any = {};
    if (
      (value.value.newValue?.type === 'ref' &&
        value.value.oldValue?.type !== value.value.newValue?.type &&
        value.value.newValue.id !== value.value.oldValue?.id) ||
      (value.value.newValue?.type !== 'ref' && value.value.newValue?.toString() !== value.value.oldValue?.toString())
    ) {
      if (value.value.column?.getColId() === 'userId' && value.value.data.addressId) {
        updateValues.addressId = undefined;
      }
      updateValues[value.value.column?.getColId()] = value.value.newValue;
      value.depDef.cellUpdate?.(updateValues, value.value.data.ref);
    }
    // this.mainStore.update?.(this.mainStore.store as InstanceType<MainStoreInstance>, event.value, event.value.data.ref);
  }
  onGridSelection(_event: IRowNode<any>[]): MaybeAsync<void> {
    throw new Error('Method not implemented.');
  }

  public ngOnInit(): void {
    effect(
      () => {
        this.mainStore.get?.(this.mainStore.store as InstanceType<MainStoreInstance>);
        if (this.gridApi) {
          this.refreshCache();
        }
      },
      { injector: this.injector },
    );
  }

  protected refreshCache(params?: RefreshServerSideParams) {
    this.gridApi?.refreshServerSide(params);
  }

  protected datasource() {
    return {
      getRows: (params: IServerSideGetRowsParams) => this.getRows(params),
    };
  }

  protected getRows(params: IServerSideGetRowsParams) {
    const resource = this.mainStore.get?.(this.mainStore.store as InstanceType<MainStoreInstance>) as Array<unknown>;
    // console.log('resource', resource, params);
    params.success({
      rowData: resource.slice(params.request.startRow, params.request.endRow),
      rowCount: resource.length,
    });
    this.gridApi?.hideOverlay();
  }
}
